import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faSearch, faSync } from '@fortawesome/free-solid-svg-icons';

const AUTO_REFRESH_INTERVAL = 3 * 60 * 1000; // 3 minutes

const FullControlSurveys = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSurveys, setTotalSurveys] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [lastRefreshResult, setLastRefreshResult] = useState(null);
  const surveysPerPage = 10;
  const refreshIntervalRef = useRef(null);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    fetchSurveys();
  }, [currentPage, activeSearchTerm]);

  // Auto-refresh on a schedule only — no on-mount refresh.
  // The refresh syncs every full-control survey from LimeSurvey (slow) which on a
  // single-threaded dev server blocks every other request including page navigation.
  // Users can refresh manually via the button if they need a fresh sync immediately.
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      refreshAssignments();
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  const refreshAssignments = useCallback(async () => {
    // Prevent concurrent refreshes
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    setRefreshing(true);

    try {
      const response = await axios.post('/control/surveys/full-control/refresh-assignments', {}, {
        timeout: 300000 // 5 minutes timeout for sync operations
      });

      const result = response.data;
      setLastRefresh(new Date());
      setLastRefreshResult(result);

      // If new assignments were created, refresh the survey list
      if (result.total_new_assignments > 0) {
        await fetchSurveysQuiet();
      }
    } catch (error) {
      console.error('Error refreshing full control assignments:', error);
    } finally {
      setRefreshing(false);
      isRefreshingRef.current = false;
    }
  }, []);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: surveysPerPage
      });

      if (activeSearchTerm) {
        params.append('search', activeSearchTerm);
      }

      const response = await axios.get(`/control/surveys/full-control?${params.toString()}`);

      if (response.data.data) {
        setSurveys(response.data.data);
        setTotalPages(response.data.last_page || 1);
        setTotalSurveys(response.data.total || response.data.data.length);
      } else {
        setSurveys(response.data);
        setTotalPages(1);
        setTotalSurveys(response.data.length);
      }
    } catch (error) {
      console.error('Error fetching full control surveys:', error);
    } finally {
      setLoading(false);
    }
  };

  // Quiet fetch that doesn't show loading spinner (for background refreshes)
  const fetchSurveysQuiet = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage,
        per_page: surveysPerPage
      });

      if (activeSearchTerm) {
        params.append('search', activeSearchTerm);
      }

      const response = await axios.get(`/control/surveys/full-control?${params.toString()}`);

      if (response.data.data) {
        setSurveys(response.data.data);
        setTotalPages(response.data.last_page || 1);
        setTotalSurveys(response.data.total || response.data.data.length);
      } else {
        setSurveys(response.data);
        setTotalPages(1);
        setTotalSurveys(response.data.length);
      }
    } catch (error) {
      console.error('Error fetching full control surveys (quiet):', error);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearch = () => {
    setActiveSearchTerm(searchTerm);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSurveyClick = (surveyId) => {
    navigate(`/control/full-control/${surveyId}/submissions`);
  };

  const formatLastRefresh = () => {
    if (!lastRefresh) return null;
    return lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #3b82f6 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6 animate-fade-in">
        <div className="max-w-7xl mx-auto">
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                  Enquêtes en Contrôle Complet
                </h1>
                <p className="text-gray-600">
                  Visualisez toutes les enquêtes en mode contrôle complet
                </p>
              </div>
              {/* Manual refresh + auto-refresh status indicator */}
              <div className="flex items-center gap-3 text-sm text-gray-500">
                {refreshing ? (
                  <span className="flex items-center gap-1.5 text-gray-500">
                    <FontAwesomeIcon icon={faSync} className="animate-spin text-xs" />
                    Synchronisation...
                  </span>
                ) : (
                  <button
                    onClick={refreshAssignments}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                    title="Synchroniser les nouvelles soumissions LimeSurvey"
                  >
                    <FontAwesomeIcon icon={faSync} />
                    Actualiser
                  </button>
                )}
                {!refreshing && lastRefresh && (
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                    Maj {formatLastRefresh()}
                    {lastRefreshResult && lastRefreshResult.total_new_assignments > 0 && (
                      <span className="text-green-600 font-medium">
                        (+{lastRefreshResult.total_new_assignments})
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-6 animate-slide-up">
        <div className="max-w-7xl mx-auto">
          {/* Search */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2">
              <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Rechercher par titre ou ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none flex-1 w-full sm:w-auto"
                />
                <button
                  onClick={handleSearch}
                  className="px-3 sm:px-4 py-2 h-[42px] min-w-[42px] sm:min-w-auto bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center flex-shrink-0"
                  title="Rechercher"
                >
                  <FontAwesomeIcon icon={faSearch} className="text-sm sm:text-base" />

                </button>
              </div>
            </div>
          </div>

          {/* Surveys Table */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Chargement des enquêtes...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Enquête
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID LimeSurvey
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Soumissions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        % Contrôle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Progression Contrôle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Affectations
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contrôlées
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        En Attente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {surveys.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                          Aucune enquête en mode contrôle complet
                        </td>
                      </tr>
                    ) : (
                      surveys.map((survey) => {
                        const controlPercentage = survey.control_percentage;
                        const controlTarget = survey.control_target;
                        const controlled = survey.done_all_control_count || 0;
                        const humanCount = survey.done_human_count || 0;
                        const aiCount = survey.done_ai_only_count || 0;
                        const remaining = survey.control_remaining;
                        const progressPercent = controlTarget && controlTarget > 0
                          ? Math.min(100, Math.round((controlled / controlTarget) * 100))
                          : null;
                        const humanWidth = controlTarget && controlTarget > 0
                          ? Math.min(100, (humanCount / controlTarget) * 100)
                          : 0;
                        const aiWidth = controlTarget && controlTarget > 0
                          ? Math.min(100 - humanWidth, (aiCount / controlTarget) * 100)
                          : 0;
                        const totalDone = humanCount + aiCount;
                        const humanPct = totalDone > 0 ? Math.round((humanCount / totalDone) * 100) : 0;
                        const aiPct = totalDone > 0 ? Math.round((aiCount / totalDone) * 100) : 0;
                        const isComplete = remaining !== null && remaining <= 0;

                        return (
                          <tr
                            key={survey.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleSurveyClick(survey.id)}
                          >
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900">{survey.title}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {survey.limesurvey_id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {survey.completed_submissions_count}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700">
                              {controlPercentage != null ? `${controlPercentage}%` : <span className="text-gray-400">-</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {controlTarget != null ? (
                                <div className="min-w-[160px]">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`font-semibold ${isComplete ? 'text-green-600' : 'text-blue-600'}`}>
                                      {controlled}/{controlTarget}
                                    </span>
                                    {remaining > 0 && (
                                      <span className="text-xs text-orange-600 font-medium ml-2">
                                        reste {remaining}
                                      </span>
                                    )}
                                    {isComplete && (
                                      <span className="text-xs text-green-600 font-medium ml-2">
                                        Atteint
                                      </span>
                                    )}
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2 flex overflow-hidden">
                                    <div
                                      className={`h-2 transition-all ${isComplete && aiCount === 0 ? 'bg-green-500' : 'bg-blue-500'}`}
                                      style={{ width: `${humanWidth}%` }}
                                      title={`Humain: ${humanCount} (${humanPct}%)`}
                                    ></div>
                                    <div
                                      className="h-2 bg-indigo-500 transition-all"
                                      style={{ width: `${aiWidth}%` }}
                                      title={`IA: ${aiCount} (${aiPct}%)`}
                                    ></div>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {survey.full_control_assignments_count || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                              {controlled}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 font-semibold">
                              {survey.pending_full_control_assignments_count || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSurveyClick(survey.id);
                                }}
                                className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
                              >
                                Détails
                                <FontAwesomeIcon icon={faChevronRight} />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
              <div className="flex items-center text-sm text-gray-700">
                <span>
                  Affichage de {((currentPage - 1) * surveysPerPage) + 1} à {Math.min(currentPage * surveysPerPage, totalSurveys)} sur {totalSurveys} enquête{totalSurveys > 1 ? 's' : ''}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                </button>

                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullControlSurveys;
